/**
 * "Work Order" section primitives — the reusable half of the invite page.
 *
 * The visual system is the trades' own paperwork: paper and ink, one accent,
 * hairline rules, a job sheet as the hero object, prices as invoice lines.
 * Tokens and component classes live in `wo.css`, scoped entirely under
 * `.wo-page` so the dashboard keeps its own system.
 *
 * Everything here is presentational — no network, no invite state. Sibling
 * blocks (e.g. the "your dashboard" preview section) compose these rather than
 * re-deriving the look, so the page cannot drift section by section.
 */

import React from 'react';
import './wo.css';

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

export function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

export function ExternalIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3H3v10h10v-3M9 3h4v4M13 3L7 9" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8.5l3.2 3L13 4.5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Shell and layout                                                    */
/* ------------------------------------------------------------------ */

/**
 * The wrapper that turns on the whole design language. Nothing outside it
 * changes — that is the point: the dashboard shares this app and must not
 * inherit a single token from here.
 */
export function WorkOrderShell({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`wo-page ${className}`.trim()}>{children}</div>;
}

export function Wrap({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`wo-wrap ${className}`.trim()}>{children}</div>;
}

/** A page chapter: hairline on top, generous vertical air, optional dark band. */
export function Section({
  children,
  dark = false,
  flush = false,
  id,
  className = '',
}: {
  children: React.ReactNode;
  /** Ink band — used once, for "why this arrived". */
  dark?: boolean;
  /** Drops the top rule (the chapter directly under the hero). */
  flush?: boolean;
  id?: string;
  className?: string;
}) {
  return (
    <section
      id={id}
      className={[
        'wo-sec',
        dark ? 'wo-sec--dark' : '',
        flush ? 'wo-sec--flush' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Wrap>{children}</Wrap>
    </section>
  );
}

/** The monospace all-caps label that opens every chapter. */
export function Eyebrow({
  children,
  light = false,
  className = '',
}: {
  children: React.ReactNode;
  light?: boolean;
  className?: string;
}) {
  return (
    <p
      className={['wo-eyebrow', light ? 'wo-eyebrow--light' : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </p>
  );
}

export function Pill({
  children,
  ok = false,
}: {
  children: React.ReactNode;
  ok?: boolean;
}) {
  return (
    <span className={`wo-pill${ok ? ' wo-pill--ok' : ''}`}>
      <i />
      {children}
    </span>
  );
}

type ButtonTone = 'ink' | 'ghost' | 'light';

const toneClass: Record<ButtonTone, string> = {
  ink: '',
  ghost: ' wo-btn--ghost',
  light: ' wo-btn--light',
};

export function WoButton({
  children,
  tone = 'ink',
  small = false,
  className = '',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: ButtonTone;
  small?: boolean;
}) {
  return (
    <button
      {...rest}
      className={`wo-btn${toneClass[tone]}${small ? ' wo-btn--sm' : ''} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

export function WoLinkButton({
  children,
  tone = 'ink',
  small = false,
  className = '',
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  tone?: ButtonTone;
  small?: boolean;
}) {
  return (
    <a
      {...rest}
      className={`wo-btn${toneClass[tone]}${small ? ' wo-btn--sm' : ''} ${className}`.trim()}
    >
      {children}
    </a>
  );
}

/* ------------------------------------------------------------------ */
/* The job sheet                                                       */
/* ------------------------------------------------------------------ */

/**
 * One `dt`/`dd` pair of the sheet — the ledger row the whole system is built
 * on. A row can carry the rubber stamp, which anchors to the row rather than
 * to the sheet: sheet height moves with the copy, row height does not.
 */
export function LedgerRow({
  label,
  stamp,
  children,
}: {
  label: string;
  stamp?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {children}
        {stamp ? <Stamp>{stamp}</Stamp> : null}
      </dd>
    </>
  );
}

/**
 * The rotated rubber stamp. Decorative — it repeats something the sheet
 * already says in words, and is never the only place a fact appears.
 * Positioned against the nearest ledger row (`.wo-sheet dd`).
 */
export function Stamp({ children }: { children: React.ReactNode }) {
  return (
    <span className="wo-stamp" aria-hidden="true">
      {children}
    </span>
  );
}

export function JobSheet({
  title = 'Job sheet',
  reference,
  children,
  footer,
  className = '',
}: {
  title?: string;
  /** Right-hand side of the sheet header — e.g. the site's own subdomain. */
  reference?: React.ReactNode;
  /** `LedgerRow`s — one of them may carry the stamp. */
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  return (
    <aside className={`wo-sheet ${className}`.trim()} aria-label={title}>
      <div className="wo-sheet-h">
        <span>{title}</span>
        {reference ? <b>{reference}</b> : null}
      </div>
      <dl>{children}</dl>
      {footer ? <div className="wo-sheet-f">{footer}</div> : null}
    </aside>
  );
}

/** The big money figure in the sheet ("Due today $0.00"). */
export function SheetTotal({
  amount,
  note,
}: {
  amount: string;
  note?: string;
}) {
  return (
    <span className="wo-big wo-num">
      {amount}
      {note ? <small>{note}</small> : null}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Browser frame                                                       */
/* ------------------------------------------------------------------ */

/**
 * The website, in a window. The frame is chrome only — whatever is passed as
 * children owns the 16:10 view (on the invite that is `ScrollPreviewImage`,
 * kept exactly as it was).
 */
export function BrowserFrame({
  url,
  badge,
  children,
  className = '',
}: {
  url: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`wo-browser ${className}`.trim()}>
      <div className="wo-bbar">
        <i />
        <i />
        <i />
        <span className="wo-url">{url}</span>
        {badge}
      </div>
      <div className="wo-bview">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dark-band ledger (facts list)                                       */
/* ------------------------------------------------------------------ */

export function FactList({
  items,
}: {
  items: { label: string; body: React.ReactNode }[];
}) {
  return (
    <ul className="wo-facts">
      {items.map(item => (
        <li key={item.label}>
          <span>{item.label}</span>
          <span>{item.body}</span>
        </li>
      ))}
    </ul>
  );
}
